import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Button, Toast, useViewport, Typography, Spinner, Card } from '@chilecompra/react-kit/components'
import { useEffectOnce, putCookie, getCookie } from '@chilecompra/react-kit'
import {
  closeModal,
  onResetRegisterWizardState,
  openModal,
  registerBuyerThunk,
  onSaveTemporaryLegalRepresentation,
  onGetDownloadFileThunk,
  registerFlowThunk,
  onDownloadAgreementCollaborationPdfThunk
} from '../RegisterWizard.actions'
import {
  isLocalEnv,
  REACT_APP_ATTACHMENT_API_URL,
  REACT_APP_BUSINESS,
  REACT_APP_ID_BUSINESS,
  REACT_APP_SUB_BUSINESS,
  REACT_APP_UNIQUE_PASS_SEED
} from '../../../config/settings/environment'
import { useCsrfToken } from '../../../modules/hooks/security'
import {
  AttachmentSubTitle,
  DesktopNavigation,
  MobileButtons,
  MobileNavigation,
  Root,
  Section,
  Title,
  CheckBox,
  Conditions,
  Link,
  AttachmentButtonSection,
  AttachmentSection,
  DeleteIcon,
  DeleteTypography
} from './RegisterWizardCollaborationVolunteers.styles'
import SignerIdentificationContainer from './SignerIdentification.container'
import AgreementInformationContainer from './AgreementInformation.container'
import SuccessModal from './RegisterWizardCollaborationVolunteers.successModal'
import useRequestInterceptor from '../../../modules/hooks/useRequestInterceptor'
import { onSignInUniquePassThunk } from '../../UniquePassAuth/UniquePassAuth.actions'
import { useRecaptcha } from '../../../modules/hooks/recaptcha'

const Attachments = lazy(() => import('remote/FrontAdjuntos'))

/**
 * The RegisterWizardCollaborationVolunteers' container.
 */
const RegisterWizardCollaborationVolunteers = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { size } = useViewport()
  const { executeRecaptcha } = useRecaptcha()
  const csrfToken = useCsrfToken(REACT_APP_UNIQUE_PASS_SEED)
  const isSignOperationOK = getCookie('signWithCU')
  const flow = 'INICIO_PASO_5_BIS'

  const [files, setFiles] = useState([])
  const [checked, setChecked] = useState(false)
  const [openModalAttachResolution, setOpenModalAttachResolution] = useState(false)
  const [openModalDeleteAllResolution, setOpenModalDeleteAllResolution] = useState(false)

  const { error: errorSign } = useSelector(state => state.uniquePassAuth)
  const modals = useSelector(state => state.registerWizard.modals)
  const {
    registerBuyerLoading,
    loading,
    error,
    finishInscription: { error: directorError },
    business,
    signerIdentification,
    collaborationAgreementPdf
  } = useSelector(state => state.registerWizard)
  const documentValue = useSelector(state => state.registerWizard.documentValue)
  const downloadFileUrl = useSelector(state => state.registerWizard.downloadFileUrl)

  const apiUrl = `${REACT_APP_ATTACHMENT_API_URL}/adjuntos-api/${REACT_APP_BUSINESS}/${REACT_APP_SUB_BUSINESS}/${REACT_APP_ID_BUSINESS}`
  useRequestInterceptor(apiUrl, setFiles)

  const canNotNextButtonClick = useCallback(() => {
    const noFilesUploaded = files.length === 0
    const noDocumentValue = !documentValue

    return !checked || registerBuyerLoading || noFilesUploaded || noDocumentValue
  }, [checked, registerBuyerLoading, files, documentValue])

  const disabledNextButton = canNotNextButtonClick()
  const isAttachmentButtonDisabled = files.length > 0

  const handleNext = () => {
    if (!isLocalEnv) {
      putCookie('signWithCU', 'true')
      dispatch(onSignInUniquePassThunk({ csrfToken, isSignAgreement: true }))
    } else {
      dispatch(openModal('successAgreement'))
    }
  }

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [dispatch])

  const handleConfirmAction = useCallback(
    id => {
      return () => {
        dispatch(
          registerBuyerThunk({
            executeRecaptcha,
            onSuccessCallback: () => {
              onChangeStep('complete')
              dispatch(closeModal(id))
            }
          })
        )
      }
    },
    [dispatch, onChangeStep]
  )

  const handleCloseModal = useCallback(
    id => {
      return () => {
        dispatch(closeModal(id))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (files.length > 0) {
      dispatch(onSaveTemporaryLegalRepresentation({ files }))
    }
  }, [files, dispatch])

  const fetchDownloadUrl = useCallback(() => {
    if (files.length > 0 && files[0].iddoc) {
      dispatch(onGetDownloadFileThunk({ iddoc: files[0].iddoc }))
    }
  }, [files, dispatch])

  const handleDeleteFile = () => {
    setFiles([])
  }

  const handleDownloadAgreementPdf = () => {
    if (!collaborationAgreementPdf.loading) {
      dispatch(
        onDownloadAgreementCollaborationPdfThunk({
          agreementText: documentValue,
          business,
          signer: signerIdentification.signer
        })
      )
    }
  }

  useEffect(() => {
    if (files.length > 0 && files[0].filename) {
      fetchDownloadUrl()
    }
  }, [files, dispatch, fetchDownloadUrl])

  useEffect(() => {
    if (isSignOperationOK && isSignOperationOK === 'true') {
      setChecked(true)
      if (!error && !errorSign) {
        dispatch(openModal('successAgreement'))
      }
    }
  }, [isSignOperationOK, dispatch, error, errorSign])

  return (
    <Root>
      <Section container>
        <Section item lg={12} xs={12}>
          <Title tabIndex="0" aria-label="Convenio de adhesión">
            Convenio de adhesión
          </Title>
          {!error && (
            <>
              <SignerIdentificationContainer />
              <AgreementInformationContainer />
              <Conditions>
                <CheckBox
                  tabIndex="0"
                  aria-label="Declaro que toda la información ingresada es veraz, completa, verificable y actualizada"
                  checked={checked}
                  onChange={e => setChecked(e.target.checked)}
                  color="primary"
                />
                Declaro que toda la información ingresada en este formulario de inscripción es veraz, completa,
                verificable y se encuentra actualizada
              </Conditions>

              <Card
                color="blank"
                margin="20px 0"
                height={size.isTiny ? '272px' : size.isDownToLarge ? '275px' : '123px'}
                enableBorder
              >
                <AttachmentSection disabled={isAttachmentButtonDisabled}>
                  <Section tabIndex="0" width={size.isTiny ? '312px' : '350px'}>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      margin={size.isDownToLarge ? '40px 0 0 0' : size.isTiny ? '50px 0 0 0' : '0 0 10px 0'}
                    >
                      Adjuntar documento personería
                    </Typography>
                    <Typography margin="5px 0" variant="body2">
                      Documento que indica que la persona firmante tiene la facultad de obligar a la Entidad. Por
                      ejemplo: resoluciones, escrituras, etc.
                    </Typography>
                  </Section>
                  <Section width={size.isTiny ? '312px' : '156px'} height="100px">
                    <AttachmentSubTitle tabIndex="0">
                      Se admiten archivos con un peso máximo de 20mb.
                    </AttachmentSubTitle>
                  </Section>
                  <AttachmentButtonSection>
                    <Suspense fallback={<Spinner />}>
                      <Attachments
                        aria-label="Adjuntar archivo"
                        business={REACT_APP_BUSINESS}
                        idBusiness={REACT_APP_ID_BUSINESS}
                        subBusiness={REACT_APP_SUB_BUSINESS}
                        color="blank"
                        typeView="list"
                        openModal={openModalAttachResolution}
                        openModalDeleteAll={openModalDeleteAllResolution}
                        setOpenModal={setOpenModalAttachResolution}
                        setOpenModalDeleteAll={setOpenModalDeleteAllResolution}
                        WRITE
                        LIST
                        WRITEBULK
                        DELETEBULK={false}
                        DELETE={false}
                        READ={false}
                        READBULK={false}
                      />
                    </Suspense>
                  </AttachmentButtonSection>
                </AttachmentSection>
              </Card>

              {files.length > 0 && (
                <Card color="blank" enableBorder margin="20px 0">
                  <Section display="flex" justifyContent="space-between" alignItems="center">
                    <Section>
                      {files && files.length > 0 && (
                        <Typography variant="body2">
                          <Link
                            tabIndex="0"
                            aria-label={`Descargar el archivo ${files[0].filename}`}
                            href={downloadFileUrl}
                          >
                            {files[0].filename}
                          </Link>
                        </Typography>
                      )}
                    </Section>
                    <Section
                      tabIndex="0"
                      aria-label="Eliminar"
                      onClick={handleDeleteFile}
                      display="flex"
                      alignItems="center"
                      gap="7px"
                      margin="20px 0px 0 0"
                    >
                      <DeleteTypography>Eliminar</DeleteTypography>
                      <DeleteIcon />
                    </Section>
                  </Section>

                  <Section />
                </Card>
              )}

              {!size.isTiny && (size.isDownToLarge || size.isUpToLarge) && (
                <Section container>
                  <DesktopNavigation>
                    <Button
                      color="primary"
                      disabled={disabledNextButton}
                      loading={loading}
                      margin={!size.isDownToLarge ? '20px 0 0 0' : ''}
                      type="button"
                      onClick={handleNext}
                      tabIndex="0"
                      aria-label="Firmar con Clave Única"
                    >
                      Firmar con Clave Única
                    </Button>
                  </DesktopNavigation>
                </Section>
              )}
              {size.isTiny && (
                <Section container>
                  <MobileNavigation>
                    <MobileButtons
                      color="primary"
                      disabled={disabledNextButton}
                      loading={loading}
                      type="button"
                      onClick={handleNext}
                      tabIndex="0"
                      aria-label="Firmar con Clave Única"
                    >
                      Firmar con Clave Única
                    </MobileButtons>
                  </MobileNavigation>
                </Section>
              )}
            </>
          )}

          {errorSign && errorSign === 'SOMETHING_WENT_WRONG_ERROR' && (
            <Toast tabIndex="0" severity="warning">
              Error en servicio externo, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}

          {directorError && directorError === 'SOMETHING_WENT_WRONG_ERROR' && (
            <Toast tabIndex="0" severity="warning">
              Error en servicio externo, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}

          {errorSign && errorSign === 'UNDERAGE_USER_ERROR' && (
            <Toast
              tabIndex="0"
              severity="warning"
              margin={size.isTiny ? '22px 0 13px 0' : '34px 0 0'}
              padding="16px 24px"
            >
              El Rut ingresado corresponde a una persona menor de edad según la información del Registro Civil.
            </Toast>
          )}

          {errorSign && errorSign === 'DECEASED_PERSON_ERROR' && (
            <Toast
              tabIndex="0"
              severity="warning"
              margin={size.isTiny ? '22px 0 13px 0' : '34px 0 0'}
              padding="16px 24px"
            >
              El Rut ingresado corresponde a una persona fallecida según la información del Registro Civil.
            </Toast>
          )}

          {errorSign && errorSign === 'DNI_NOT_MATCH_ERROR' && (
            <Toast tabIndex="0" severity="warning">
              El RUT del firmante no coincide con el RUT autentificado con Clave Única.
              <br />
              Revisa los datos ingresados en el paso anterior.
            </Toast>
          )}

          {error && error?.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
            <Toast tabIndex="0" severity="warning">
              Error en servicio externo, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}

          {error && error?.reason === 'SII_ERROR' && (
            <Toast tabIndex="0" severity="warning">
              En estos momentos no podemos acceder a la información debido a un problema de conexión con el Servicio de
              Impuestos Internos, puedes intentar nuevamente en unos minutos para continuar con tu inscripción..
            </Toast>
          )}

          {error && (
            <>
              {!size.isTiny && (size.isDownToLarge || size.isUpToLarge) && (
                <Section container margin="33px 0 0">
                  <DesktopNavigation>
                    <Button tabIndex="0" aria-label="Cancelar" onClick={handleBack} type="button" variant="outlined">
                      Cancelar
                    </Button>
                  </DesktopNavigation>
                </Section>
              )}
              {size.isTiny && (
                <Section container margin="28px 0 0 0">
                  <MobileNavigation>
                    <MobileButtons
                      tabIndex="0"
                      aria-label="Cancelar"
                      onClick={handleBack}
                      type="button"
                      variant="outlined"
                    >
                      Cancelar
                    </MobileButtons>
                  </MobileNavigation>
                </Section>
              )}
            </>
          )}
        </Section>
      </Section>
      <SuccessModal
        loadingConfirmation={registerBuyerLoading}
        loadingAgreement={collaborationAgreementPdf.loading}
        open={modals.successAgreement?.open}
        onClose={handleCloseModal('successAgreement')}
        confirmAction={handleConfirmAction('successAgreement')}
        downloadAgreementAction={handleDownloadAgreementPdf}
      />
    </Root>
  )
}

RegisterWizardCollaborationVolunteers.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardCollaborationVolunteers.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardCollaborationVolunteers
